import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Vyhrazená tlaková zařízení - V1 DRAFT - authored for structural
 * completeness; NOT a substitute for sign-off by a Czech osoba odborně
 * způsobilá v prevenci rizik. Legal refs: z. 250/2021 Sb. (o bezpečnosti
 * práce v souvislosti s provozem vyhrazených technických zařízení) + NV
 * 192/2022 Sb. (vyhrazená tlaková zařízení). AWARENESS level: this course
 * teaches recognition + safe behaviour around boilers, expansion and storage
 * vessels; actual operation of a vyhrazené tlakové zařízení additionally
 * requires documented zaškolení (and for boilers a topičský průkaz), and
 * inspections/tests belong to a revizní technik. Bilingual (cs-CZ + en-US).
 * Three chapters (co je VTZ tlakové | bezpečnostní výstroj | meze laika) +
 * a graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload.
 */
export const pressureVesselsAwarenessProfile: TrainingProfile = {
  slug: 'pressure-vessels-awareness',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Vyhrazená tlaková zařízení',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-co-je-vtz',
        title: t('Co je vyhrazené tlakové zařízení', 'What a reserved pressure system is'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-co-je-vtz',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Tlak je neviditelné riziko', 'Pressure is an invisible hazard'),
          },
          {
            id: 'p-co-je-vtz-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'V kotelnách a technických místnostech potkáš **vyhrazená tlaková zařízení**: parní a horkovodní **kotle**, **tlakové nádoby stabilní** (expanzní nádoby, zásobníkové ohřívače, vzdušníky kompresorů) a pojistná zařízení k nim. Nádoba pod tlakem má energii jako stlačená pružina - **selhání znamená roztržení, opaření nebo výbuch**, ne jen „kapající vodu".',
              'In boiler rooms and plant rooms you will meet **reserved pressure systems**: steam and hot-water **boilers**, **stationary pressure vessels** (expansion vessels, storage water heaters, compressor air receivers) and their safety devices. A vessel under pressure holds energy like a compressed spring - **failure means rupture, scalding or explosion**, not just "a dripping leak".',
            ),
          },
          {
            id: 'c-co-je-vtz-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **z. 250/2021 Sb.** + **NV 192/2022 Sb.** Tento kurz je **seznámení** - obsluha vyhrazeného tlakového zařízení vyžaduje navíc **doložené zaškolení** u konkrétního zařízení (u kotlů topičský průkaz) a revize provádí **revizní technik**.',
              'Legal basis: **Act 250/2021 Coll.** + **Gov. Reg. 192/2022 Coll.** This course is **awareness** - operating a reserved pressure system additionally requires **documented on-equipment instruction** (for boilers, a boiler attendant licence), and inspections are done by a **certified inspection technician**.',
            ),
          },
          {
            id: 'p-co-je-vtz-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Instalatér se s tlakem potká nejčastěji u **expanzní nádoby a zásobníkového ohřívače**: i „obyčejný" bojler je nádoba, kterou před zásahem **odtlakuješ a necháš vychladnout**. Koroze u hrdel, vyboulení pláště nebo stopy po přetlaku jsou důvod práci **zastavit**, ne přetěsnit.',
              'A plumber meets pressure most often at the **expansion vessel and the storage water heater**: even an "ordinary" cylinder is a vessel you **depressurise and let cool before working on it**. Corrosion at the nozzles, a bulging shell or signs of over-pressure are a reason to **stop the work**, not to re-seal it.',
            ),
          },
          {
            id: 'cl-co-je-vtz',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Kotle, expanzní nádoby, zásobníky = tlaková zařízení',
                'Boilers, expansion vessels, storage heaters = pressure systems',
              ),
              t(
                'Před zásahem odtlakovat a nechat vychladnout',
                'Depressurise and cool before any intervention',
              ),
              t('Vyboulení/koroze = zastavit práci', 'Bulging/corrosion = stop the work'),
              t('Obsluha jen s doloženým zaškolením', 'Operation only with documented instruction'),
            ],
          },
        ],
      },
      {
        id: 'ch-vystroj',
        title: t('Bezpečnostní výstroj', 'Safety equipment'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-vystroj'],
        blocks: [
          {
            id: 'h-vystroj',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Pojistný ventil se nepodkládá', 'The safety valve is never blocked'),
          },
          {
            id: 'p-vystroj-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Každé tlakové zařízení chrání **bezpečnostní výstroj**: **pojistný ventil** (odpustí přetlak), **manometr** (ukazuje tlak vůči vyznačené nejvyšší dovolené hodnotě) a **omezovače teploty**. Pojistný ventil se **nikdy nevyřazuje, nepodkládá ani „nedotahuje", protože kape** - kapání je příznak závady nebo přetlaku, který ventil právě řeší. Počet pojistných ventilů vyřazených za provozu: **nula**.',
              'Every pressure system is protected by **safety equipment**: the **safety valve** (relieves over-pressure), the **pressure gauge** (shows pressure against the marked maximum allowable value) and **temperature limiters**. The safety valve is **never disabled, wedged or "tightened up" because it drips** - dripping is a symptom of a defect or of the very over-pressure the valve is handling. Number of safety valves that may be out of action during operation: **zero**.',
            ),
          },
          {
            id: 'anim-vystroj',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'pressureRelief',
            steps: [
              {
                label: t('Ventil odpouští', 'The valve relieves'),
                say: t(
                  'Pojistný ventil odpustí přetlak - nádoba drží.',
                  'The safety valve vents the over-pressure - the vessel holds.',
                ),
              },
              {
                label: t('Podložený ventil', 'Wedged valve'),
                say: t(
                  'Zablokovaný ventil: nádoba se roztrhne.',
                  'A blocked valve: the vessel ruptures.',
                ),
              },
            ],
            caption: t(
              'Ručička manometru stoupá k červenému poli - pojistný ventil odfoukne přetlak. Zablokovaný ventil = roztržení nádoby.',
              'The gauge needle climbs toward the red zone - the safety valve blows off the excess. A blocked valve = a ruptured vessel.',
            ),
          },
          {
            id: 'p-vystroj-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Než u zařízení začneš pracovat, **přečti manometr**: ručička v červeném poli nebo nad vyznačenou ryskou znamená **nezasahovat a volat obsluhu/servis**. Všímej si i pojistného ventilu vyústěného „do prázdna" - odfuk musí být sveden tak, aby nikoho neopařil.',
              'Before working near the equipment, **read the gauge**: a needle in the red zone or above the marked line means **do not intervene - call the operator/service**. Also watch for a safety valve discharging "into the open" - the blow-off must be piped so it cannot scald anyone.',
            ),
          },
          {
            id: 'kc-vystroj',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Pojistný ventil na bojleru odkapává. Co to znamená?',
              'The safety valve on a water heater is dripping. What does that mean?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t(
                  'Nic - stačí ho podložit nebo dotáhnout',
                  'Nothing - just wedge or tighten it',
                ),
              },
              {
                id: 'kc-b',
                label: t(
                  'Příznak přetlaku nebo závady - řešit příčinu',
                  'A symptom of over-pressure or a defect - address the cause',
                ),
              },
              {
                id: 'kc-c',
                label: t(
                  'Ventil je zbytečně citlivý, vyměnit za silnější',
                  'The valve is oversensitive - fit a stronger one',
                ),
              },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Ventil kape, protože právě odpouští přetlak (např. vadná expanzní nádoba). Umlčet ventil = odstranit poslední pojistku před roztržením.',
              'It drips because it is relieving over-pressure (e.g. a failed expansion vessel). Silencing the valve removes the last safeguard before rupture.',
            ),
          },
        ],
      },
      {
        id: 'ch-meze',
        title: t('Meze laika a revize', 'Limits of the layperson, and inspections'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-zavada'],
        blocks: [
          {
            id: 'h-meze',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Věz, kdy předat dál', 'Know when to hand over'),
          },
          {
            id: 'p-meze-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Revize, tlakové zkoušky a zkoušky pojistných ventilů** provádí výhradně **revizní technik vyhrazených tlakových zařízení**. Obsluhu (najíždění, odstavování kotle) smí dělat jen **zaškolená a pověřená osoba** - zaškolení musí být **doložené zápisem**, ne „ukázal mi to kolega". Tvoje role u cizího zařízení: pozorovat, nezasahovat, hlásit.',
              '**Inspections, pressure tests and safety-valve tests** are done exclusively by a **certified pressure-equipment inspection technician**. Operation (starting up, shutting down a boiler) is only for a **trained and authorised person** - the instruction must be **documented in writing**, not "a colleague showed me". Your role at someone else\'s equipment: observe, do not intervene, report.',
            ),
          },
          {
            id: 'c-meze-varovani',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Najdeš-li zařízení s prošlou revizí, přetlakem nebo zablokovaným pojistným ventilem, **nezprovozňuj ho** - zdokumentuj stav, informuj zákazníka a nahlas to v aplikaci. Zprovoznění vadného tlakového zařízení padá na tebe.',
              'If you find equipment with an expired inspection, over-pressure or a blocked safety valve, **do not put it into service** - document the state, inform the customer and report it in the app. Commissioning defective pressure equipment lands on you.',
            ),
          },
          {
            id: 'ord-zavada',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď postup při zjištění závady na tlakovém zařízení.',
              'Order the steps on finding a pressure-equipment defect.',
            ),
            items: [
              {
                id: 'oz-1',
                label: t(
                  'Nezasahovat a zajistit, aby nikdo nebyl v ohrožení',
                  'Do not intervene; keep everyone out of danger',
                ),
              },
              {
                id: 'oz-2',
                label: t(
                  'Zdokumentovat stav (foto, odečet manometru)',
                  'Document the state (photo, gauge reading)',
                ),
              },
              {
                id: 'oz-3',
                label: t('Informovat zákazníka / provozovatele', 'Inform the customer / operator'),
              },
              {
                id: 'oz-4',
                label: t(
                  'Nahlásit v aplikaci a doporučit revizního technika',
                  'Report in the app and refer an inspection technician',
                ),
              },
            ],
          },
          {
            id: 'k-meze',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**Tlaková nádoba = stlačená pružina.** Před zásahem odtlakovat a zchladit, pojistný ventil nikdy neumlčovat, revize patří reviznímu technikovi a obsluha zaškolené osobě.',
              '**A pressure vessel is a compressed spring.** Depressurise and cool before intervening, never silence the safety valve; inspections belong to the inspection technician and operation to a trained, authorised person.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-revize-single',
      kind: 'single',
      chapterId: 'ch-meze',
      prompt: t(
        'Kdo smí provést revizi tlakové nádoby?',
        'Who may carry out the inspection of a pressure vessel?',
      ),
      choices: [
        {
          id: 'a',
          label: t(
            'Revizní technik vyhrazených tlakových zařízení',
            'A certified pressure-equipment inspection technician',
          ),
        },
        {
          id: 'b',
          label: t(
            'Kterýkoli instalatér s tímto e-kurzem',
            'Any plumber who completed this e-course',
          ),
        },
        { id: 'c', label: t('Provozovatel zařízení svépomocí', 'The operator, on their own') },
      ],
      correct: 'a',
    },
    {
      id: 'q-ventil-tf',
      kind: 'truefalse',
      chapterId: 'ch-vystroj',
      prompt: t(
        'Kapající pojistný ventil je v pořádku dočasně podložit, aby nekapal.',
        'It is fine to temporarily wedge a dripping safety valve so it stops dripping.',
      ),
      correct: false,
    },
    {
      id: 'q-vtz-multi',
      kind: 'multi',
      chapterId: 'ch-co-je-vtz',
      prompt: t(
        'Co patří mezi tlaková zařízení, se kterými se instalatér běžně potká? (vyber všechny správné)',
        'Which of these are pressure systems a plumber commonly meets? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Expanzní nádoba topení', 'A heating expansion vessel') },
        {
          id: 'b',
          label: t('Zásobníkový ohřívač vody (bojler)', 'A storage water heater (cylinder)'),
        },
        { id: 'c', label: t('Okapový svod', 'A rainwater downpipe') },
        { id: 'd', label: t('Vzdušník kompresoru', 'A compressor air receiver') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-ventily-numeric',
      kind: 'numeric',
      chapterId: 'ch-vystroj',
      prompt: t(
        'Kolik pojistných ventilů smí být za provozu zařízení vyřazeno z činnosti?',
        'How many safety valves may be out of action while the equipment operates?',
      ),
      correct: 0,
      tolerance: 0,
    },
    {
      id: 'q-zavada-order',
      kind: 'ordering',
      chapterId: 'ch-meze',
      prompt: t('Seřaď postup při zjištění závady.', 'Order the steps on finding a defect.'),
      choices: [
        { id: 'a', label: t('Nezasahovat, zajistit okolí', 'Do not intervene; secure the area') },
        { id: 'b', label: t('Zdokumentovat stav', 'Document the state') },
        { id: 'c', label: t('Informovat zákazníka', 'Inform the customer') },
        { id: 'd', label: t('Nahlásit a doporučit revizi', 'Report and refer an inspection') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
